import { extractImageFrames, clampTo } from '../../tools';
import type { baiduModelConfig } from '../types';


export const pixverseConfigs: Record<string, baiduModelConfig> = {
    'pixverse-c1': {
        // https://tcnxnzs113h4.feishu.cn/wiki/ZS7XwAHOniYfUPkMbYmcl961nTx
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'none') {
                return '/v3/aigc/pv/video/text/generate';
            } else if (frameMode === 'first') {
                return '/v3/aigc/pv/video/img/generate';
            } else if (frameMode === 'first_last') {
                return '/v3/aigc/pv/video/transition/generate';
            } else if (frameMode === 'ref') {
                return '/v3/aigc/pv/video/fusion/generate';
            } else {
                throw new Error('Invalid frame mode');
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            // 首帧模式
            if (frameMode === 'first') {
                const [firstImage] = extractImageFrames(imagePrompt);
                return {
                    model: 'c1',
                    prompt: prompt.slice(0, 2048),
                    negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2048) : undefined,
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    quality: clampTo(quality || '720p', ['360p', '540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 首帧 URL
                    image_list: [
                        {
                            image_url: firstImage,
                        }
                    ],
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                    style: typeof providerOptions?.style === 'string' ? providerOptions.style : undefined,
                    // system
                    motion_mode: undefined,
                    effect: undefined,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                const [firstImage, lastImage] = extractImageFrames(imagePrompt);
                return {
                    model: 'c1',
                    prompt: prompt.slice(0, 2048),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    quality: clampTo(quality || '720p', ['360p', '540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 首帧 URL
                    first_frame_img: {
                        image_url: firstImage,
                    },
                    // 尾帧 URL
                    last_frame_img: {
                        image_url: lastImage,
                    },
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                    // system
                    motion_mode: undefined,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                // 图像参数
                const images = (imagePrompt ?? []).filter(Boolean).slice(0, 7);
                // 返回 Body 数据
                return {
                    model: 'c1',
                    prompt: prompt.slice(0, 2048),
                    aspect_ratio: aspectRatio === 'match' ? undefined :  clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    quality: clampTo(quality || '720p', ['360p' ,'540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 参考帧 URL
                    image_references: images.map((image_url, i) => ({
                        type: 'subject',
                        image_url: image_url,
                        ref_name: `image${i + 1}`,
                    })),
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                };
            // 文生视频模式
            } else {
                return {
                    model: 'c1',
                    prompt: prompt.slice(0, 2048),
                    negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2048) : undefined,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    quality: clampTo(quality || '720p', ['360p', '540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                    // system
                    motion_mode: undefined,
                    effect: undefined,
                    sound_effect_switch: undefined,
                    sound_effect_content: undefined,
                };
            }
        },
    },
    'pixverse-v6': {
        // https://tcnxnzs113h4.feishu.cn/wiki/ZS7XwAHOniYfUPkMbYmcl961nTx
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'none') {
                return '/v3/aigc/pv/video/text/generate';
            } else if (frameMode === 'first') {
                return '/v3/aigc/pv/video/img/generate';
            } else if (frameMode === 'first_last') {
                return '/v3/aigc/pv/video/transition/generate';
            } else {
                throw new Error('Invalid frame mode');
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            // 首帧模式
            if (frameMode === 'first') {
                const [firstImage] = extractImageFrames(imagePrompt);
                return {
                    model: 'v6',
                    prompt: prompt.slice(0, 2048),
                    negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2048) : undefined,
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    quality: clampTo(quality || '720p', ['360p', '540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 首帧 URL
                    image_list: [
                        {
                            image_url: firstImage,
                        }
                    ],
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                    style: typeof providerOptions?.style === 'string' ? providerOptions.style : undefined,
                    // system
                    motion_mode: undefined,
                    effect: undefined,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                const [firstImage, lastImage] = extractImageFrames(imagePrompt);
                return {
                    model: 'v6',
                    prompt: prompt.slice(0, 2048),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    quality: clampTo(quality || '720p', ['360p', '540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 首帧 URL
                    first_frame_img: {
                        image_url: firstImage,
                    },
                    // 尾帧 URL
                    last_frame_img: {
                        image_url: lastImage,
                    },
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                    // system
                    motion_mode: undefined,
                };
            // 文生视频模式
            } else {
                return {
                    model: 'v6',
                    prompt: prompt.slice(0, 2048),
                    negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2048) : undefined,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    quality: clampTo(quality || '720p', ['360p', '540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                    // system
                    motion_mode: undefined,
                    effect: undefined,
                    sound_effect_switch: undefined,
                    sound_effect_content: undefined,
                };
            }
        },
    },
    'pixverse-v5.6': {
        // https://tcnxnzs113h4.feishu.cn/wiki/ZS7XwAHOniYfUPkMbYmcl961nTx
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'none') {
                return '/v3/aigc/pv/video/text/generate';
            } else if (frameMode === 'first') {
                return '/v3/aigc/pv/video/img/generate';
            } else if (frameMode === 'first_last') {
                return '/v3/aigc/pv/video/transition/generate';
            } else if (frameMode === 'ref') {
                return '/v3/aigc/pv/video/fusion/generate';
            } else {
                throw new Error('Invalid frame mode');
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, audioUri, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            // 首帧模式
            if (frameMode === 'first') {
                const [firstImage] = extractImageFrames(imagePrompt);
                return {
                    model: 'v5.6',
                    prompt: prompt.slice(0, 2048),
                    negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2048) : undefined,
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const, 5),
                    quality: clampTo(quality || '720p', ['360p', '540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 首帧 URL
                    image_list: [
                        {
                            image_url: firstImage,
                        }
                    ],
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                    style: typeof providerOptions?.style === 'string' ? providerOptions.style : undefined,
                    thinking_type: typeof providerOptions?.thinking_type === 'string' ? providerOptions.thinking_type : undefined,
                    // system
                    motion_mode: undefined,
                    effect: undefined,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                const [firstImage, lastImage] = extractImageFrames(imagePrompt);
                return {
                    model: 'v5.6',
                    prompt: prompt.slice(0, 2048),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const, 5),
                    quality: clampTo(quality || '720p', ['360p', '540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 首帧 URL
                    first_frame_img: {
                        image_url: firstImage,
                    },
                    // 尾帧 URL
                    last_frame_img: {
                        image_url: lastImage,
                    },
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                    // system
                    motion_mode: undefined,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                const images = (imagePrompt ?? []).filter(Boolean).slice(0, 7);
                return {
                    model: 'v5.6',
                    prompt: prompt.slice(0, 2048),
                    aspect_ratio: aspectRatio === 'match' ? undefined :  clampTo(aspectRatio || '16:9', ['16:9', '9:16', '4:3', '3:4', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const, 5),
                    quality: clampTo(quality || '720p', ['360p' ,'540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 参考帧 URL
                    image_references: images.map((image_url, i) => ({
                        type: 'subject',
                        image_url: image_url,
                        ref_name: `image${i + 1}`,
                    })),
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                };
            // 文生视频模式
            } else {
                return {
                    model: 'v5.6',
                    prompt: prompt.slice(0, 2048),
                    negative_prompt: typeof providerOptions?.negative_prompt === 'string' ? (providerOptions.negative_prompt as string).slice(0, 2048) : undefined,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '4:3', '3:4', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const, 5),
                    quality: clampTo(quality || '720p', ['360p', '540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 可选参数
                    generate_audio_switch: typeof providerOptions?.generate_audio_switch === 'boolean' ? providerOptions.generate_audio_switch : true,
                    thinking_type: typeof providerOptions?.thinking_type === 'string' ? providerOptions.thinking_type : undefined,
                    // system
                    motion_mode: undefined,
                    effect: undefined,
                };
            }
        },
    },
};